import { z } from "zod";

export const PLATFORMS = [
  "TikTok",
  "Instagram",
  "YouTube",
  "Facebook",
  "X",
  "Spotify",
  "Threads",
] as const;

export type Platform = (typeof PLATFORMS)[number];

const trimmedRequired = (max: number) =>
  z.string().trim().min(1, "Required").max(max, `Must be ${max} characters or fewer`);

const trimmedOptional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, `Must be ${max} characters or fewer`)
    .optional()
    .or(z.literal(""))
    .transform((v) => (v ? v : undefined));

/**
 * Order intake form. Validated identically on the client (for UX) and here
 * on the server (for correctness) — the server copy is the one that matters.
 */
export const orderInputSchema = z.object({
  artistName: trimmedRequired(120),
  songTitle: trimmedRequired(120),
  primaryGenre: trimmedRequired(60),
  description: trimmedRequired(2000),
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(254),

  lyrics: trimmedOptional(12000),
  releaseDate: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD")
    .optional()
    .or(z.literal(""))
    .transform((v) => (v ? v : undefined)),
  secondaryGenre: trimmedOptional(60),
  mood: trimmedOptional(200),
  targetAudience: trimmedOptional(300),
  influences: trimmedOptional(500),
  platforms: z.array(z.enum(PLATFORMS)).max(PLATFORMS.length).default([]),
  importantLines: trimmedOptional(1000),
  notes: trimmedOptional(2000),

  // Cloudflare Turnstile response token, verified server-side.
  turnstileToken: z.string().min(1, "Verification required").optional(),
});

export type OrderInput = z.infer<typeof orderInputSchema>;

export const orderStatusValues = [
  "pending",
  "paid",
  "queued",
  "generating",
  "preparing_package",
  "complete",
  "failed",
] as const;
export type OrderStatusValue = (typeof orderStatusValues)[number];

/** Public-safe projection returned by the order status API. Never includes
 * internal IDs, Stripe identifiers, or other customers' data. */
export const publicOrderStatusSchema = z.object({
  status: z.enum(orderStatusValues),
  artistName: z.string(),
  songTitle: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  downloadReady: z.boolean(),
  errorMessage: z.string().nullable(),
});

export type PublicOrderStatus = z.infer<typeof publicOrderStatusSchema>;
